import { type IHosAvailability, type TDiscreteAvailabilitySummary, HOURS, timeSpanToMs } from "hos-tools";
import { IAlertsAvailability } from "../store/data/types";

export const ALERT_THRESHOLDS = [0, 0.5, 1, 2] as const;

export const AVAILABILITY_KEYS_TO_IGNORE = [
    "driver", "recap", "cycleAvailabilities", "userRuleSetToComplete",
    "drivingBreakDuration", "is16HourExemptionAvailable", "isAdverseDrivingExemptionAvailable", "isAdverseDrivingApplied",
    "isOffDutyDeferralExemptionAvailable", "isRailroadExemptionAvailable", "restLabel", "exemptionReducedRest"
];

/**
 * Converts a PascalCase discrete availability summary (keys in PascalCase, values in ms)
 * to a normalized map of camelCase keys to hours, filtering out ignored keys and undefined values.
 * Negative ms values (driver already in violation) are clamped to 0 so they appear as "0m left"
 * in the upcoming violation dialog, matching the pre-MYG-117339 aggregate availability behaviour.
 */
export function normalizeDiscreteAvailability(discrete: TDiscreteAvailabilitySummary): Record<string, number> {
    const result: Record<string, number> = {};
    for (const pascalKey of Object.keys(discrete)) {
        const camelKey = pascalKey.charAt(0).toLowerCase() + pascalKey.slice(1);
        const ms = discrete[pascalKey as keyof TDiscreteAvailabilitySummary];
        if (ms === undefined) {
            continue;
        }
        result[camelKey] = Math.max(0, ms) / HOURS;
    }
    return result;
}

/**
 * Converts an aggregate IHosAvailability (keys in camelCase, values as .NET TimeSpan strings)
 * to a normalized map of camelCase keys to hours, filtering out ignored keys and non-string values.
 */
export function normalizeAggregateAvailability(aggregate: IHosAvailability, additionalKeysToIgnore?: string[]): Record<string, number> {
    const result: Record<string, number> = {};
    for (const key of Object.keys(aggregate)) {
        if (AVAILABILITY_KEYS_TO_IGNORE.indexOf(key) !== -1) {
            continue;
        }
        if (additionalKeysToIgnore && additionalKeysToIgnore.indexOf(key) !== -1) {
            continue;
        }
        const value = aggregate[key as keyof IHosAvailability];
        if (!value || typeof value !== "string") {
            continue;
        }
        result[key] = timeSpanToMs(value) / HOURS;
    }
    return result;
}

export interface ITriggeredAlert {
    key: string;
    threshold: number;
    availabilityHours: number;
}

export interface IAlertComputationResult {
    triggeredAlerts: ITriggeredAlert[];
    updatedAlerts: IAlertsAvailability;
    shouldRemoveDialog: boolean;
}

/**
 * Pure function that determines which availability alerts should fire given current
 * availability values (key → hours) and the previously logged alert state.
 *
 * For each key:
 *   - If availability dropped below a threshold that hasn't been logged, trigger an alert
 *     and mark that threshold (and all higher ones) as logged.
 *   - If availability recovered above the max threshold (2h), reset all logged thresholds.
 *
 * Returns the triggered alerts, updated state, and whether a dialog reset is needed.
 */
export function computeAvailabilityAlerts(
    availability: Record<string, number>,
    currentAlerts: IAlertsAvailability
): IAlertComputationResult {
    const maxThreshold = ALERT_THRESHOLDS[ALERT_THRESHOLDS.length - 1];

    // Deep-copy to avoid mutating the caller's state
    const updatedAlerts: IAlertsAvailability = JSON.parse(JSON.stringify(currentAlerts));

    const triggeredAlerts: ITriggeredAlert[] = [];
    let shouldRemoveDialog = false;

    for (const key of Object.keys(availability)) {
        const hours = availability[key];

        if (!updatedAlerts[key]) {
            const defaults: { [k: number]: boolean } = {};
            for (const t of ALERT_THRESHOLDS) {
                defaults[t] = false;
            }
            updatedAlerts[key] = defaults;
        }
        const keyAlerts = updatedAlerts[key] as { [k: number]: boolean };

        // Reset all thresholds if availability recovered above the max
        if (hours > maxThreshold) {
            for (const t of ALERT_THRESHOLDS) {
                if (keyAlerts[t]) {
                    keyAlerts[t] = false;
                    shouldRemoveDialog = true;
                }
            }
            continue;
        }

        // Find the lowest crossed threshold that hasn't been alerted yet
        // ALERT_THRESHOLDS is in ascending order, so the first match is the lowest
        const crossedThreshold = ALERT_THRESHOLDS.find(t => hours <= t && !keyAlerts[t]);
        if (crossedThreshold === undefined) {
            continue;
        }

        triggeredAlerts.push({ key, threshold: crossedThreshold, availabilityHours: hours });

        // Mark this threshold and all higher ones as alerted
        for (const t of ALERT_THRESHOLDS) {
            if (t >= crossedThreshold) {
                keyAlerts[t] = true;
            }
        }
    }

    return { triggeredAlerts, updatedAlerts, shouldRemoveDialog };
}
